/**
 * AudioRecorder - Web Audio recorder for team chat
 *
 * States: idle -> recording -> stopped (preview)
 * Uses MediaRecorder API with microphone permission handling
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Play, Pause, Send, X, Loader2, AlertCircle } from 'lucide-react';

interface AudioRecorderProps {
  onSend: (blob: Blob, durationSec: number) => void;
  onCancel: () => void;
  uploading?: boolean;
}

type RecorderState = 'idle' | 'recording' | 'stopped';

export function AudioRecorder({ onSend, onCancel, uploading = false }: AudioRecorderProps) {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioBlobRef = useRef<Blob | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const startTimeRef = useRef<number>(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current = null;
      }
    };
  }, []);

  // Auto-start recording on mount
  useEffect(() => {
    startRecording();
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        audioBlobRef.current = blob;
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setState('stopped');

        // Stop microphone
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };

      recorder.start(200); // collect data every 200ms
      startTimeRef.current = Date.now();
      setState('recording');

      // Start timer
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 200);
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        setError('Permesso microfono negato. Abilita il microfono nelle impostazioni del browser.');
      } else {
        setError('Impossibile accedere al microfono.');
      }
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const handleSend = useCallback(() => {
    if (audioBlobRef.current) {
      onSend(audioBlobRef.current, elapsed);
    }
  }, [onSend, elapsed]);

  const handleCancel = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    onCancel();
  }, [onCancel, audioUrl]);

  const togglePlayback = useCallback(() => {
    if (!audioUrl) return;

    if (isPlaying && audioElRef.current) {
      audioElRef.current.pause();
      setIsPlaying(false);
      return;
    }

    const audio = new Audio(audioUrl);
    audioElRef.current = audio;
    audio.onended = () => setIsPlaying(false);
    audio.play();
    setIsPlaying(true);
  }, [audioUrl, isPlaying]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Error state
  if (error) {
    return (
      <div
        className="flex items-center gap-3 px-4 py-3 rounded-2xl"
        style={{ backgroundColor: '#FEF2F2', border: '1px solid rgba(239,68,68,0.2)' }}
      >
        <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#EF4444' }} strokeWidth={1.5} />
        <span className="text-sm flex-1" style={{ color: '#991B1B' }}>{error}</span>
        <button
          onClick={onCancel}
          className="p-1.5 rounded-full transition-colors"
          style={{ color: '#787774' }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  // Recording state
  if (state === 'recording') {
    return (
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Pulsing red dot */}
        <div className="relative flex items-center justify-center w-5 h-5 flex-shrink-0">
          <div
            className="absolute w-3 h-3 rounded-full animate-ping"
            style={{ backgroundColor: 'rgba(239,68,68,0.4)' }}
          />
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: '#EF4444' }}
          />
        </div>

        {/* Timer */}
        <span
          className="text-sm font-medium tabular-nums flex-1"
          style={{ color: '#37352F' }}
        >
          {formatTime(elapsed)}
        </span>

        {/* Cancel */}
        <button
          onClick={handleCancel}
          className="p-1.5 rounded-full transition-colors"
          style={{ color: '#787774' }}
          title="Annulla"
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>

        {/* Stop */}
        <button
          onClick={stopRecording}
          className="p-2 rounded-full text-white transition-colors"
          style={{ backgroundColor: '#EF4444' }}
          title="Ferma registrazione"
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#DC2626')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#EF4444')}
        >
          <Square className="w-4 h-4" fill="white" strokeWidth={0} />
        </button>
      </div>
    );
  }

  // Stopped state (preview)
  if (state === 'stopped') {
    return (
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Play / Pause preview */}
        <button
          onClick={togglePlayback}
          className="p-2 rounded-full transition-colors flex-shrink-0"
          style={{ backgroundColor: 'rgba(35,131,226,0.1)', color: '#2383E2' }}
          title={isPlaying ? 'Pausa' : 'Riproduci'}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(35,131,226,0.18)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(35,131,226,0.1)')}
        >
          {isPlaying
            ? <Pause className="w-4 h-4" strokeWidth={1.5} />
            : <Play className="w-4 h-4" strokeWidth={1.5} />
          }
        </button>

        {/* Duration */}
        <div className="flex-1 flex items-center gap-2">
          {/* Simple waveform placeholder */}
          <div className="flex items-center gap-[2px] flex-1 h-5">
            {Array.from({ length: 24 }).map((_, i) => (
              <div
                key={i}
                className="flex-1 rounded-full"
                style={{
                  height: `${30 + Math.sin(i * 0.8) * 40 + Math.random() * 30}%`,
                  backgroundColor: 'rgba(35,131,226,0.3)',
                  minWidth: 2,
                  maxWidth: 4,
                }}
              />
            ))}
          </div>
          <span
            className="text-xs font-medium tabular-nums flex-shrink-0"
            style={{ color: '#787774' }}
          >
            {formatTime(elapsed)}
          </span>
        </div>

        {/* Cancel */}
        <button
          onClick={handleCancel}
          disabled={uploading}
          className="p-1.5 rounded-full transition-colors disabled:opacity-40"
          style={{ color: '#787774' }}
          title="Annulla"
          onMouseEnter={(e) => !uploading && (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>

        {/* Send */}
        <button
          onClick={handleSend}
          disabled={uploading}
          className="p-2 rounded-full text-white transition-colors disabled:opacity-60"
          style={{ backgroundColor: '#2383E2' }}
          title="Invia audio"
          onMouseEnter={(e) => !uploading && (e.currentTarget.style.backgroundColor = '#1a6cb8')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2383E2')}
        >
          {uploading
            ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
            : <Send className="w-4 h-4" strokeWidth={1.5} />
          }
        </button>
      </div>
    );
  }

  // Idle — should not normally be visible since we auto-start, but just in case
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <button
        onClick={startRecording}
        className="p-2 rounded-full transition-colors"
        style={{ backgroundColor: 'rgba(35,131,226,0.1)', color: '#2383E2' }}
        title="Avvia registrazione"
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(35,131,226,0.18)')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(35,131,226,0.1)')}
      >
        <Mic className="w-5 h-5" strokeWidth={1.5} />
      </button>
      <span className="text-sm" style={{ color: '#787774' }}>Premi per registrare</span>
      <button
        onClick={onCancel}
        className="ml-auto p-1.5 rounded-full transition-colors"
        style={{ color: '#787774' }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}
